import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { salesIntelligenceService } from '../services/api';
import {
  TrendingUp, Users, FileText, DollarSign, Activity, Loader2, RefreshCw,
  ChevronRight, Lock, Clock, CheckCircle, XCircle, AlertTriangle, Eye,
} from 'lucide-react';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString() : '—'; } catch { return '—'; } };
const dur = (m) => (m && m.available ? `${m.hours} hrs` : null);

const Metric = ({ label, value, sub, testid, accent = 'text-gray-900' }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={testid}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${accent}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const Unavailable = ({ label, reason, testid }) => (
  <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4" data-testid={testid} title={reason}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-400 mt-1">Unavailable</p>
    <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{reason}</p>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <section className="space-y-3">
    <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2"><Icon className="w-5 h-5 text-blue-600" /> {title}</h2>
    {children}
  </section>
);

const FollowUpGroup = ({ title, group, icon: Icon, accent, dateKey, dateLabel }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`followup-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <span className="text-sm font-medium text-gray-900 flex items-center gap-2"><Icon className={`w-4 h-4 ${accent}`} /> {title}</span>
      <span className="text-xs font-semibold bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">{group?.count || 0}</span>
    </div>
    {(group?.items || []).length === 0 ? (
      <p className="px-4 py-4 text-xs text-gray-400">Nothing here right now.</p>
    ) : (
      <ul className="divide-y divide-gray-50 max-h-56 overflow-y-auto">
        {group.items.map((q) => (
          <li key={q.quote_id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`followup-item-${q.quote_id}`}>
            <div className="min-w-0">
              <p className="font-medium text-gray-800 truncate">{q.customer_name}</p>
              <p className="text-xs text-gray-400">#{q.quote_number} · {dateLabel} {fmtDate(q[dateKey])}</p>
            </div>
            <span className="font-semibold text-gray-900 whitespace-nowrap ml-3">{money(q.total)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const SalesIntelligencePage = () => {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [followUp, setFollowUp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, f] = await Promise.all([salesIntelligenceService.getDashboard(), salesIntelligenceService.getFollowUp()]);
      setData(d); setFollowUp(f); setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      else setError('Failed to load sales intelligence.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (disabled) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto text-center py-20" data-testid="sales-intel-disabled-notice">
          <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Revenue &amp; Sales Intelligence is not active</h2>
          <p className="text-gray-600 mt-2">Enable the <code className="text-sm bg-gray-100 px-1 rounded">quote_reporting_enabled</code> feature flag (Soft Go-Live) to view the dashboard.</p>
        </div>
      </DashboardLayout>
    );
  }

  const li = data?.lead_intelligence;
  const qi = data?.quote_intelligence;
  const ri = data?.revenue_intelligence;
  const bi = data?.business_intelligence;
  const un = data?.unavailable || {};
  const maxFunnel = Math.max(1, ...((data?.conversion_funnel || []).map((s) => s.count)));

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="sales-intelligence-page">
        <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/admin/dashboard" className="hover:text-gray-700" data-testid="sales-intel-breadcrumb-home">Admin Panel</Link>
          <ChevronRight className="w-4 h-4 mx-1" /><span className="text-gray-900 font-medium">Sales Intelligence</span>
        </nav>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><TrendingUp className="w-7 h-7 text-blue-600" /> Revenue &amp; Sales Intelligence</h1>
            <p className="text-gray-600 mt-1">Executive view across leads, quotes, and revenue.</p>
          </div>
          <button onClick={load} data-testid="sales-intel-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-24" data-testid="sales-intel-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : data ? (
          <>
            <Section title="Lead Intelligence" icon={Users}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Metric label="New Leads" value={li.new} testid="li-new" />
                <Metric label="Contacted" value={li.contacted} testid="li-contacted" />
                <Metric label="Qualified" value={li.qualified} testid="li-qualified" />
                <Metric label="Assigned" value={li.assigned} testid="li-assigned" />
                <Metric label="Lost" value={li.lost} testid="li-lost" />
                <Metric label="Conversion %" value={pct(li.conversion_rate)} testid="li-conversion" accent="text-emerald-600" />
              </div>
            </Section>

            <Section title="Quote Intelligence" icon={FileText}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Metric label="Created" value={qi.created} testid="qi-created" />
                <Metric label="Sent" value={qi.sent} testid="qi-sent" />
                <Metric label="Viewed" value={qi.viewed} testid="qi-viewed" />
                <Metric label="Accepted" value={qi.accepted} testid="qi-accepted" accent="text-emerald-600" />
                <Metric label="Declined" value={qi.declined} testid="qi-declined" />
                <Metric label="Expired" value={qi.expired} testid="qi-expired" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Metric label="Acceptance Rate" value={pct(qi.acceptance_rate)} testid="qi-acceptance-rate" accent="text-blue-600" />
                <Metric label="Avg. Time to First View" value={dur(qi.avg_time_to_first_view) || 'Insufficient data'} sub={qi.avg_time_to_first_view.available ? `${qi.avg_time_to_first_view.sample_size} quote(s)` : ''} testid="qi-ttfv" />
                <Metric label="Avg. Time to Acceptance" value={dur(qi.avg_time_to_acceptance) || 'Insufficient data'} sub={qi.avg_time_to_acceptance.available ? `${qi.avg_time_to_acceptance.sample_size} quote(s)` : ''} testid="qi-tta" />
              </div>
            </Section>

            <Section title="Conversion Funnel" icon={Activity}>
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="conversion-funnel">
                {(data.conversion_funnel || []).map((s) => (
                  <div key={s.stage} className="flex items-center gap-3">
                    <span className="w-32 text-sm text-gray-600 shrink-0">{s.stage}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full flex items-center justify-end pr-2 transition-all" style={{ width: `${Math.max(4, (s.count / maxFunnel) * 100)}%` }}>
                        <span className="text-xs font-semibold text-white">{s.count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Revenue Intelligence" icon={DollarSign}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Metric label="Estimated Quote Value" value={money(ri.estimated_quote_value)} testid="ri-estimated" />
                <Metric label="Accepted Quote Value" value={money(ri.accepted_quote_value)} testid="ri-accepted" accent="text-emerald-600" />
                <Unavailable label="Revenue Collected" reason={un.revenue_collected} testid="ri-revenue-collected" />
                <Unavailable label="Forecast Revenue" reason={un.forecast_revenue} testid="ri-forecast" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="ri-by-service">
                  <p className="text-sm font-medium text-gray-900 mb-2">Revenue by Service (accepted)</p>
                  {(ri.revenue_by_service || []).length === 0 ? <p className="text-xs text-gray-400">No accepted revenue yet.</p> : (
                    <ul className="space-y-1">{ri.revenue_by_service.map((r) => <li key={r.key} className="flex justify-between text-sm"><span className="text-gray-600">{r.label}</span><span className="font-semibold text-gray-900">{money(r.value)}</span></li>)}</ul>
                  )}
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="ri-by-month">
                  <p className="text-sm font-medium text-gray-900 mb-2">Revenue by Month (accepted)</p>
                  {(ri.revenue_by_month || []).length === 0 ? <p className="text-xs text-gray-400">No accepted revenue yet.</p> : (
                    <ul className="space-y-1">{ri.revenue_by_month.map((r) => <li key={r.month} className="flex justify-between text-sm"><span className="text-gray-600">{r.month}</span><span className="font-semibold text-gray-900">{money(r.value)}</span></li>)}</ul>
                  )}
                </div>
              </div>
            </Section>

            <Section title="Business Intelligence" icon={TrendingUp}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="bi-top-sources">
                  <p className="text-sm font-medium text-gray-900 mb-2">Top Lead Sources</p>
                  {(bi.top_lead_sources || []).length === 0 ? <p className="text-xs text-gray-400">No data.</p> : (
                    <ul className="space-y-1">{bi.top_lead_sources.slice(0, 5).map((r) => <li key={r.key} className="flex justify-between text-sm"><span className="text-gray-600">{r.label}</span><span className="font-semibold text-gray-900">{r.count}</span></li>)}</ul>
                  )}
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="bi-top-requested">
                  <p className="text-sm font-medium text-gray-900 mb-2">Top Requested Services</p>
                  {(bi.top_requested_services || []).length === 0 ? <p className="text-xs text-gray-400">No data.</p> : (
                    <ul className="space-y-1">{bi.top_requested_services.slice(0, 5).map((r) => <li key={r.key} className="flex justify-between text-sm"><span className="text-gray-600">{r.label}</span><span className="font-semibold text-gray-900">{r.count}</span></li>)}</ul>
                  )}
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="bi-top-performing">
                  <p className="text-sm font-medium text-gray-900 mb-2">Top Performing Services</p>
                  {(bi.top_performing_services || []).length === 0 ? <p className="text-xs text-gray-400">No accepted revenue yet.</p> : (
                    <ul className="space-y-1">{bi.top_performing_services.slice(0, 5).map((r) => <li key={r.key} className="flex justify-between text-sm"><span className="text-gray-600">{r.label}</span><span className="font-semibold text-gray-900">{money(r.value)}</span></li>)}</ul>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Metric label="Avg. Quote Turnaround" value={dur(bi.average_quote_turnaround) || 'Insufficient data'} sub="Lead created → first quote" testid="bi-turnaround" />
                <Unavailable label="Average Response Time" reason={un.average_response_time} testid="bi-response-time" />
              </div>
            </Section>

            {followUp && (
              <Section title="Sales Follow-Up Queue" icon={Clock}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="follow-up-queues">
                  <FollowUpGroup title="Awaiting Response" group={followUp.awaiting_response} icon={Clock} accent="text-amber-500" dateKey="sent_at" dateLabel="sent" />
                  <FollowUpGroup title="Never Viewed" group={followUp.never_viewed} icon={Eye} accent="text-gray-500" dateKey="sent_at" dateLabel="sent" />
                  <FollowUpGroup title="Viewed Not Accepted" group={followUp.viewed_not_accepted} icon={Eye} accent="text-indigo-500" dateKey="viewed_at" dateLabel="viewed" />
                  <FollowUpGroup title="Expiring Soon" group={followUp.expiring_soon} icon={AlertTriangle} accent="text-rose-500" dateKey="valid_until" dateLabel="valid until" />
                  <FollowUpGroup title="Recently Accepted" group={followUp.recently_accepted} icon={CheckCircle} accent="text-emerald-500" dateKey="accepted_at" dateLabel="accepted" />
                  <FollowUpGroup title="Recently Declined" group={followUp.recently_declined} icon={XCircle} accent="text-rose-500" dateKey="declined_at" dateLabel="declined" />
                </div>
              </Section>
            )}

            <p className="text-xs text-gray-400 text-center pt-2">Generated {new Date(data.generated_at).toLocaleString()}. Values reflect quote amounts, not collected cash.</p>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
};

export default SalesIntelligencePage;
